import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import {
  marketingHref,
  newTabProps,
} from "@/components/marketing/marketingLinks";
import { OUT_QUAD, SWING_TO } from "@/components/marketing/easing";
import { HEADING_2 } from "@/components/marketing/marketingType";
import {
  ACCENT_DEEP,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { LandingSection } from "../LandingPrimitives";
import { sectionPad } from "./landingRun";

type WhyWebshare = Laid<"whyWebshare">;
type Feature = WhyWebshare["features"][number];

/** `.why-websh-item_icon`: the arrow badge every feature card carries. Site chrome, not page copy. */
const CARD_ARROW = "/marketing/campaign/why-webshare-arrow.svg";

/** The cards' own lift, from `.why-websh_item`. */
const CARD_SHADOW = "0 16px 30px -11px rgba(27, 23, 80, 0.05)";

/** The mint the banner's highlight and button are set in. */
const BANNER_MINT = "#54EACC";

/**
 * The published hover, an interaction rather than a stylesheet rule: the rule
 * darkens and the arrow fades and swings in, and both ease back on the way
 * out.
 */
const ARROW_SELECTOR = "& .why-webshare-arrow";

/** `.why-websh_item` and `.why-websh_banner` share their box. */
const TILE = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  height: "448px",
  p: "40px 40px 0",
  borderRadius: "20px",
  overflow: "hidden",
  [NOT_WIDE]: { height: "auto" },
} satisfies SxProps<Theme>;

/** `.why-websh_row` (2fr 1fr) and `.why-websh_second-row` (1fr 2fr): one column below the wide step. */
function row(columns: string): SxProps<Theme> {
  return {
    display: "grid",
    gridTemplateColumns: columns,
    gap: "24px",
    [NOT_WIDE]: { gridTemplateColumns: "1fr" },
  };
}

/** `.why-websh_item`: a linked card, its words over its screenshot. */
function FeatureCard({ feature }: { feature: Feature }) {
  return (
    <Box
      component="a"
      href={marketingHref(feature.href)}
      {...newTabProps(feature)}
      sx={[
        TILE,
        {
          border: "1px solid white",
          backgroundColor: "common.white",
          color: marketingPalette.inkSubtle,
          boxShadow: CARD_SHADOW,
          textDecoration: "none",
          transition: `border-color 300ms ${OUT_QUAD}`,
          "&:hover": {
            borderColor: marketingPalette.darkGround,
            [ARROW_SELECTOR]: {
              opacity: 1,
              transform: "scale(1)",
              transition: `opacity 200ms ${OUT_QUAD}, transform 500ms ${SWING_TO}`,
            },
          },
          [NOT_WIDE]: { p: "24px" },
          [TINY]: { gap: "16px", p: "16px" },
        },
      ]}
    >
      <Box
        component="img"
        className="why-webshare-arrow"
        src={CARD_ARROW}
        alt=""
        loading="lazy"
        sx={{
          position: "absolute",
          top: "32px",
          right: "32px",
          width: "32px",
          height: "32px",
          opacity: 0,
          transform: "scale(0.8)",
          transition: `opacity 200ms ${OUT_QUAD}, transform 300ms ${OUT_QUAD}`,
          [MID]: { top: "16px", right: "16px" },
          [TINY]: { width: "25.6px" },
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          [MID]: { width: "90%" },
        }}
      >
        <Box
          sx={{
            color: marketingPalette.ink,
            fontSize: 24,
            fontWeight: 500,
            lineHeight: "24px",
            [TINY]: { fontSize: 22.4, lineHeight: "22.4px" },
          }}
        >
          {feature.title}
        </Box>
        {feature.body ? (
          <Box sx={{ opacity: 0.8, fontSize: 15.04, lineHeight: "24.064px" }}>
            {feature.body}
          </Box>
        ) : null}
      </Box>
      {feature.image ? (
        <Box
          component="img"
          src={feature.image}
          alt=""
          loading="lazy"
          sx={{ ...SCREEN, [NOT_WIDE]: { display: "none" } }}
        />
      ) : null}
      {feature.mobileImage ? (
        <Box
          component="img"
          src={feature.mobileImage}
          alt=""
          loading="lazy"
          sx={{ ...SCREEN, display: "none", [NOT_WIDE]: { display: "block" } }}
        />
      ) : null}
    </Box>
  );
}

/** `.why-websh-item_img`. */
const SCREEN = {
  maxWidth: "100%",
  boxShadow: "0 -4px 40px -20px rgba(154, 161, 169, 0.5)",
} satisfies SxProps<Theme>;

/** `.api_overlay`: the fades over the dark card's top and bottom edges. */
function Fade({ bottom }: { bottom?: boolean }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        height: "80px",
        zIndex: 2,
        ...(bottom
          ? {
              bottom: 0,
              backgroundImage: `linear-gradient(rgba(7, 23, 39, 0), ${marketingPalette.darkGround})`,
            }
          : {
              top: 0,
              backgroundImage: `linear-gradient(${marketingPalette.darkGround}, rgba(7, 23, 39, 0))`,
            }),
      }}
    />
  );
}

/** `.why-web-item.is-proxies`: the dark card, a picture and the "API" pill over it. */
function ApiCard({ api }: { api: NonNullable<WhyWebshare["api"]> }) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        width: 1,
        maxHeight: "448px",
        p: "16px",
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: marketingPalette.darkGround,
        [NOT_WIDE]: { maxHeight: "480px" },
        [MID]: { height: "400px" },
      }}
    >
      {api.image ? (
        <Box
          component="img"
          src={api.image}
          alt=""
          loading="lazy"
          sx={{ maxWidth: "100%" }}
        />
      ) : null}
      {api.link ? (
        <Box
          component="a"
          href={marketingHref(api.link.href ?? "")}
          {...newTabProps(api.link)}
          sx={{
            position: "absolute",
            right: "16px",
            bottom: "16px",
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            p: "6.08px 12px",
            borderRadius: "100vw",
            backgroundColor: "common.white",
            color: marketingPalette.ink,
            fontSize: 14.08,
            fontWeight: 500,
            textDecoration: "none",
            boxShadow: "0 2px 7px rgba(9, 25, 40, 0.13)",
            transition: "all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
            "&:hover": { color: ACCENT_DEEP },
          }}
        >
          {api.link.label}
          <ArrowIcon />
        </Box>
      ) : null}
      <Fade bottom />
      <Fade />
    </Box>
  );
}

/** `.why-websh_bg-main_blur-*`: the two blurred lights under the banner's words. */
function Glow({
  color,
  className,
  sx,
}: {
  color: string;
  className: string;
  sx: SxProps<Theme>;
}) {
  return (
    <Box
      className={className}
      sx={[
        {
          position: "absolute",
          width: "668.96px",
          height: "260px",
          borderRadius: "1000vw",
          backgroundColor: color,
          opacity: 0.5,
          filter: "blur(70px)",
          transition: `transform 300ms ${OUT_QUAD}, opacity 300ms ${OUT_QUAD}`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

/**
 * The banner's hover, an interaction too: the words swell to 1.05 and the two
 * lights rise by 30% and 47% of their own height as they brighten, all
 * swinging in and easing back out over 300.
 */
const BANNER_HOVER = {
  "& .why-webshare-banner-words": {
    transform: "scale(1.05)",
    transition: `transform 250ms ${SWING_TO}`,
  },
  "& .why-webshare-glow-green": {
    transform: "translateY(-30%)",
    opacity: 0.59,
    transition: `transform 500ms ${SWING_TO}, opacity 500ms ${SWING_TO}`,
  },
  "& .why-webshare-glow-blue": {
    transform: "translateY(-47%)",
    opacity: 0.63,
    transition: `transform 500ms ${SWING_TO}, opacity 500ms ${SWING_TO}`,
  },
} as const;

/** `.why-websh_banner`: the whole dark tile links to sign-up. */
function Banner({ banner }: { banner: NonNullable<WhyWebshare["banner"]> }) {
  return (
    <Box
      component="a"
      href={marketingHref(banner.link?.href ?? "")}
      {...newTabProps(banner.link ?? {})}
      sx={[
        TILE,
        {
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          textDecoration: "none",
          "&:hover": BANNER_HOVER,
          [NOT_WIDE]: { pt: "96px", pb: "96px" },
          [TINY]: { p: "64px 24px" },
        },
      ]}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundColor: marketingPalette.darkGround,
        }}
      >
        <Glow
          color="#15FFD0"
          className="why-webshare-glow-green"
          sx={{
            bottom: "-44%",
            left: "-34%",
            [NOT_WIDE]: {
              width: "352px",
              height: "128px",
              bottom: "-27%",
              left: "-11%",
            },
          }}
        />
        <Glow
          color="#2C67FF"
          className="why-webshare-glow-blue"
          sx={{
            bottom: "-45%",
            left: "44%",
            [NOT_WIDE]: {
              width: "352px",
              height: "128px",
              bottom: "-22%",
              left: "62%",
            },
          }}
        />
      </Box>
      <Box
        className="why-webshare-banner-words"
        sx={{
          position: "relative",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "24px",
          width: 1,
          maxWidth: "448px",
          textAlign: "center",
          transition: `transform 300ms ${OUT_QUAD}`,
        }}
      >
        <Typography
          component="h3"
          sx={{
            color: "common.white",
            fontSize: 32,
            fontWeight: 500,
            lineHeight: 1.3,
            letterSpacing: "-0.04em",
            [TINY]: { fontSize: 24, lineHeight: "32px" },
          }}
        >
          {/* One text node with its space, as the published heading sets it. */}
          {`${banner.title} `}
          {banner.highlight ? (
            <Box component="span" sx={{ color: BANNER_MINT }}>
              {banner.highlight}
            </Box>
          ) : null}
        </Typography>
        <BannerControl banner={banner} />
      </Box>
    </Box>
  );
}

/** `.main_header_button_holder`: the mint control and the notes beside it, inside the linked tile. */
function BannerControl({
  banner,
}: {
  banner: NonNullable<WhyWebshare["banner"]>;
}) {
  const notes = banner.reassurances ?? [];
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        rowGap: "8px",
        width: "283.04px",
        [TINY]: { width: 1 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: 1,
          height: "48px",
          px: "16px",
          borderRadius: "8px",
          backgroundColor: BANNER_MINT,
          color: "#060F1B",
          transition: "padding-left 0.2s",
        }}
      >
        {banner.link?.label}
        <ArrowIcon />
      </Box>
      {notes.length ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            rowGap: "8px",
            width: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "6.08px",
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: 14,
              [TINY]: { justifyContent: "center", width: 1, fontSize: 12 },
            }}
          >
            {notes.map((note, index) => (
              <Box key={note} sx={{ display: "contents" }}>
                {index > 0 ? (
                  <Box
                    sx={{
                      width: "4px",
                      height: "4px",
                      borderRadius: "100vw",
                      backgroundColor: marketingPalette.inkSubtle,
                    }}
                  />
                ) : null}
                <Box>{note}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * `.why-websh_wrap`: the partner campaign pages' "Why Webshare Proxy?" grid,
 * on the grey band: two linked feature cards, then the API card beside the
 * free-plan banner. A `bare` page publishes it on white.
 */
export default function WhyWebshareBlock({ block }: { block: WhyWebshare }) {
  return (
    <LandingSection
      ground={block.bare ? undefined : "grey"}
      padding={block.pad ? sectionPad(block.pad) : "both"}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "40px",
          width: 1,
          [MID]: { gap: "24px" },
        }}
      >
        <Typography component="h2" sx={HEADING_2}>
          {block.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: 1,
          }}
        >
          <Box sx={row("2fr 1fr")}>
            {block.features.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </Box>
          {block.api || block.banner ? (
            <Box sx={row("1fr 2fr")}>
              {block.api ? <ApiCard api={block.api} /> : <Box />}
              {block.banner ? <Banner banner={block.banner} /> : null}
            </Box>
          ) : null}
        </Box>
      </Box>
    </LandingSection>
  );
}
